import { Suspense } from 'react';
import { positions, Provider as AlertProvider } from 'react-alert';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { themeConfig as themeMain } from '@block-explorer/ui';

import Root from './root';
import { CacheProvider, WalletServiceProvider } from './services';
import './i18n';
import AlertNotificationTemplate from './layouts/templates/alert-notification-template';
import { createStore } from './redux';
import { useReduxStorage } from './storage';
import AppErrorHandler from './app-error-handler';
import { ThemeProvider } from 'styled-components';
import { RootState } from 'typesafe-actions';
import { ClickProvider } from '@make-software/csprclick-ui';
import { CasperNetworkProvider } from './services/casper-network-context.tsx';
import CookieConsentBanner from './components/cookie-consent-banner/cookie-consent-banner';

(window as any)['global'] = window;

declare global {
  interface Window {
    gtag: any;
    reviveAsync: any;
  }
}

const alertOptions = {
  position: positions.BOTTOM_CENTER,
  timeout: 3000,
};

let store: ReturnType<typeof createStore>;

const ThemeWrapper = ({ children }) => {
  const { mode } = useSelector((state: RootState) => state.theme);

  return (
    <ThemeProvider theme={themeMain[mode]}>
      <SkeletonTheme
        baseColor={themeMain[mode].styleguideColors.backgroundPrimary}
        highlightColor={themeMain[mode].styleguideColors.fillSecondary}
      >
        {children}
      </SkeletonTheme>
    </ThemeProvider>
  );
};

const clickOptions = {
  appName: 'CSPR.live',
  appId: config.click_app_id,
  apiKey: 'csprclick-api-key',
  csprclickHost: config.cspr_click_host,
  casperNode: config.cspr_rpc_url,
  contentMode: 'iframe',
  iframeContainer: undefined,
  providers: config.cspr_click_wallet_providers,
};

export const App = () => {
  const { reduxStorageState, setReduxStorageState } = useReduxStorage();

  if (store == null) {
    store = createStore(reduxStorageState);
    store.subscribe(() => {
      const { wallet, currencies, theme, featureSpotlights } = store.getState();
      try {
        setReduxStorageState({
          wallet,
          currencies,
          theme,
          featureSpotlights,
        });
      } catch {
        // initialization workaround v1
      }
    });
  }

  return (
    <AppErrorHandler>
      <Suspense fallback={null}>
        <ReduxProvider store={store}>
          <ThemeWrapper>
            <CacheProvider>
              <ClickProvider options={clickOptions}>
                <CasperNetworkProvider>
                  <WalletServiceProvider>
                    <AlertProvider
                      template={AlertNotificationTemplate}
                      {...alertOptions}
                    >
                      <Root />
                      <CookieConsentBanner />
                    </AlertProvider>
                  </WalletServiceProvider>
                </CasperNetworkProvider>
              </ClickProvider>
            </CacheProvider>
          </ThemeWrapper>
        </ReduxProvider>
      </Suspense>
    </AppErrorHandler>
  );
};

export default App;
